import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import api, { setToken } from "../services/api";

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: "owner" | "manager" | "cashier" | "customer";
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  loginByPin: (pin: string) => Promise<void>;
  switchByPin: (pin: string, beforeSwitch: (nextUserId: string) => Promise<void>) => Promise<void>;
  loginByPhone: (phone: string, pin: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  login: async () => {},
  loginByPin: async () => {},
  switchByPin: async () => {},
  loginByPhone: async () => {},
  logout: () => {},
  isAuthenticated: false,
});

const AUTO_LOGOUT_HOUR = 17; // 5pm, store local time
const CHECK_EVERY_MS = 60_000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);

  const login = useCallback(async (email: string, password: string) => {
    const response = await api.post("/auth/login", { email, password });
    setToken(response.data.token);
    setUser(response.data.user);
  }, []);

  const loginByPin = useCallback(async (pin: string) => {
    const response = await api.post("/auth/pin-login", { pin });
    setToken(response.data.token);
    setUser(response.data.user);
  }, []);

  // Register SWITCH: check the PIN first, let the register tidy up while the
  // outgoing cashier is still signed in, and only then hand over. A wrong PIN
  // throws before anything has changed.
  const switchByPin = useCallback(async (pin: string, beforeSwitch: (nextUserId: string) => Promise<void>) => {
    const response = await api.post("/auth/pin-login", { pin });
    await beforeSwitch(response.data.user.id);
    setToken(response.data.token);
    setUser(response.data.user);
  }, []);

  const loginByPhone = useCallback(async (phone: string, pin: string) => {
    const response = await api.post("/auth/phone-login", { phone, pin });
    setToken(response.data.token);
    setUser(response.data.user);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  // Managers are signed out automatically at 5pm. Paula 9/14: "Let's automatically
  // log Patrick out at 5 in case he forgets." Payroll and cash deposits live on
  // his login now, so an unattended manager session on the shop floor after he
  // leaves is the thing to close off.
  //
  // Owners and cashiers are untouched - cashiers work the evening shift, and
  // logging the owner out on a timer would just be in the way.
  useEffect(() => {
    if (user?.role !== "manager") return;

    const msUntilFivePm = (): number => {
      const now = new Date();
      const five = new Date(now);
      five.setHours(AUTO_LOGOUT_HOUR, 0, 0, 0);
      // Past 5 already: a manager signing in this evening gets tomorrow's 5pm,
      // rather than being bounced straight back out at the login screen.
      if (five.getTime() <= now.getTime()) five.setDate(five.getDate() + 1);
      return five.getTime() - now.getTime();
    };

    // setTimeout tops out around 24.8 days, and a laptop that sleeps through 5pm
    // would never fire one long timer, so re-check on a short interval instead.
    const tick = window.setInterval(() => {
      if (msUntilFivePm() > CHECK_EVERY_MS) return;
      const now = new Date();
      if (now.getHours() === AUTO_LOGOUT_HOUR && now.getMinutes() < 2) logout();
    }, CHECK_EVERY_MS);

    return () => window.clearInterval(tick);
  }, [user?.role, logout]);

  return (
    <AuthContext.Provider value={{ user, login, loginByPin, switchByPin, loginByPhone, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
